// Layer bits 0...7 (inclusive) are reserved for the viewer.
// There are more unallocated bits though if needed.
// The precise "allocation" of bits to different viewer users is found in the Viewer constructor.
export const RENDER_LAYER_FORWARD = { bit: 0 };
export const RENDER_LAYER_SHADOW = { bit: 1 };
export const RENDER_LAYER_UI = { bit: 2 };
export const RENDER_LAYER_IGNORE_RAYCAST = { bit: 3 };
export const VIEWER_INTERNAL_RENDER_LAYER_SCREENSHOT = { bit: 4 };
export class RenderLayerMask {
    /**
     * Creates a render layer mask from an array of layers
     * @param layers The layers to be present in the mask
     * @returns A new layer mask
     */
    constructor(layers) {
        let mask = 0;
        for (let i = 0; i < layers.length; i++) {
            mask |= get_bitmap_of_render_layer(layers[i]);
        }
        return {
            bitmap: mask
        };
    }
    /**
     * Creates a clone of a render layer mask
     */
    static clone(layer_mask) {
        return {
            bitmap: layer_mask.bitmap
        };
    }
    /**
     * Creates a new mask containing the layers present in 'mask', but adding the given 'layer'
     * @param mask The existing mask to base the new one from
     * @param layer The layer to add
     * @returns The new mask
     */
    static add_layer(mask, layer) {
        const layer_mask = new RenderLayerMask([layer]);
        return {
            bitmap: mask.bitmap | layer_mask.bitmap
        };
    }
    /**
     * Creates a new mask containing the layers present in 'mask' but with the given 'layer' removed
     * @param mask The existing mask to base the new one from
     * @param layer The layer to remove
     * @returns The new mask
     */
    static remove_layer(mask, layer) {
        const layer_mask = new RenderLayerMask([layer]);
        return {
            bitmap: mask.bitmap & (~layer_mask.bitmap)
        };
    }
    /**
     * Checks if the layer is present in the given mask
     * @param mask The mask
     * @param layer The layer to check for
     * @returns true if the layer is present in the mask, otherwise false
     */
    static has_layer(mask, layer) {
        return (mask.bitmap & get_bitmap_of_render_layer(layer)) !== 0;
    }
}
function get_bitmap_of_render_layer(layer) {
    // We could in theory use all 32 bits, but Babylon doesn't support using the last bit.
    if (layer.bit < 0 || layer.bit >= 31) {
        console.error('Invalid render layer');
        return 0;
    }
    else {
        return 1 << layer.bit;
    }
}
/**
 * Creates a render layer mask from an array of layers
 * @deprecated Use RenderLayerMask constructor
 * @param layers The layers to be present in the mask
 * @returns A new layer mask
 */
export function create_render_layer_mask(layers) {
    let mask = 0;
    for (let i = 0; i < layers.length; i++) {
        mask |= get_bitmap_of_render_layer(layers[i]);
    }
    return {
        bitmap: mask
    };
}
/**
 * Creates a clone of a render layer mask
 * @deprecated Use RenderLayerMask.clone
 */
export function clone_render_layer_mask(mask) {
    return {
        bitmap: mask.bitmap
    };
}
/**
 * Creates a new mask containing the layers present in 'mask', but adding the given 'layer'
 * @deprecated Use RenderLayerMask.add_layer
 * @param mask The existing mask to base the new one from
 * @param layer The layer to add
 * @returns The new mask
 */
export function add_layer_to_mask(mask, layer) {
    const layer_mask = create_render_layer_mask([layer]);
    return {
        bitmap: mask.bitmap | layer_mask.bitmap
    };
}
/**
 * Creates a new mask containing the layers present in 'mask' but with the given 'layer' removed
 * @deprecated Use RenderLayerMask.remove_layer
 * @param mask The existing mask to base the new one from
 * @param layer The layer to remove
 * @returns The new mask
 */
export function remove_layer_from_mask(mask, layer) {
    const layer_mask = create_render_layer_mask([layer]);
    return {
        bitmap: mask.bitmap & (~layer_mask.bitmap)
    };
}
/**
 * Tells if you a layer is present in a mask
 * @deprecated Use RenderLayerMask.has_layer
 * @param mask The mask
 * @param layer The layer to check for
 * @returns true if the layer is present in the mask, otherwise false
 */
export function mask_includes_layer(mask, layer) {
    return (mask.bitmap & get_bitmap_of_render_layer(layer)) !== 0;
}
export const RENDER_LAYER_MASK_ALL = { bitmap: 2147483647 };
export const RENDER_LAYER_MASK_NONE = { bitmap: 0 };
export const RENDER_LAYER_MASK_RAYCASTABLE = { bitmap: 2147483639 };
export const RENDER_LAYER_MASK_FORWARD = new RenderLayerMask([RENDER_LAYER_FORWARD]);
export const RENDER_LAYER_MASK_IGNORE_RAYCAST = new RenderLayerMask([RENDER_LAYER_IGNORE_RAYCAST]);
// A layer mask describing what you'll normally see in your viewport
export const RENDER_LAYER_MASK_DEFAULT = new RenderLayerMask([RENDER_LAYER_FORWARD, RENDER_LAYER_UI]);
//# sourceMappingURL=RenderLayer.js.map